import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Building2, GraduationCap, Home } from 'lucide-react';
import ShortCard from './ShortCard';

const LEVEL_BADGES = {
  beginner: { label: 'Beginner', variant: 'success' },
  intermediate: { label: 'Intermediate', variant: 'warm' },
  advanced: { label: 'Advanced', variant: 'accent' },
};

const STATUS_BADGES = {
  approved: { label: 'Enrolled', variant: 'success' },
  pending: { label: 'Pending', variant: 'warm' },
  published: { label: 'New', variant: 'new' },
  completed: { label: 'Completed', variant: 'success' },
};

export function CourseShortCard({
  course,
  enrollment,
  index = 0,
  to,
  onClick,
  showCampus = true,
  adminOverlay,
}) {
  const level = (course.level || '').toLowerCase();
  const badges = [];
  if (enrollment?.status && STATUS_BADGES[enrollment.status]) {
    badges.push(STATUS_BADGES[enrollment.status]);
  } else if (LEVEL_BADGES[level]) {
    badges.push(LEVEL_BADGES[level]);
  } else if ((course.status || '').toLowerCase() === 'published') {
    badges.push(STATUS_BADGES.published);
  }

  const providerName = showCampus
    ? (course.campus_name || course.instructor_name || 'Learnly')
    : (course.instructor_name || 'Learnly Academy');

  return (
    <ShortCard
      to={to}
      onClick={onClick}
      index={index}
      image={course.thumbnail}
      imageFallback={<BookOpen size={36} />}
      badges={badges}
      providerLogo={(
        <span className="ux-short-provider-icon">
          {showCampus ? <Building2 size={14} /> : <GraduationCap size={14} />}
        </span>
      )}
      providerName={providerName}
      title={course.title || 'Untitled course'}
      typeLabel={`Course · ${course.duration ? `${course.duration} min` : level || 'Program'}`}
    >
      {adminOverlay}
    </ShortCard>
  );
}

export function EnrollmentShortCard({ enrollment, index = 0 }) {
  const progress = Math.round(enrollment.progress_percentage || 0);
  const status = enrollment.status || 'pending';

  return (
    <ShortCard
      to={`/enrollments/${enrollment.id}`}
      index={index}
      image={enrollment.course_thumbnail}
      imageFallback={<BookOpen size={36} />}
      badges={STATUS_BADGES[status] ? [STATUS_BADGES[status]] : []}
      providerLogo={<span className="ux-short-provider-icon"><Home size={14} /></span>}
      providerName={enrollment.campus_name || 'Your learning path'}
      title={enrollment.course_title || 'Course'}
      typeLabel={`${progress}% complete`}
      footer={(
        <div className="ux-short-progress">
          <div className="ux-short-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      )}
    />
  );
}

export function CampusShortCard({ campus, index = 0, selected, onClick, isCurrent }) {
  const badges = [];
  if (isCurrent) badges.push({ label: 'Current', variant: 'success' });
  if (selected && !isCurrent) badges.push({ label: 'Selected', variant: 'info' });

  return (
    <ShortCard
      onClick={isCurrent ? undefined : onClick}
      index={index}
      selected={selected || isCurrent}
      image={campus.logo_url}
      imageFallback={<Building2 size={36} />}
      badges={badges}
      providerLogo={<span className="ux-short-provider-icon"><Building2 size={14} /></span>}
      providerName={`Campus #${campus.id}`}
      title={campus.name}
      typeLabel={campus.total_students != null ? `${campus.total_students} learners` : 'Organisation'}
    />
  );
}

export function AssessmentShortCard({ assessment, index = 0 }) {
  const type = (assessment.assessment_type || 'quiz').replace(/_/g, ' ');
  return (
    <ShortCard
      to={`/assessments/${assessment.id}/take`}
      index={index}
      imageFallback={<GraduationCap size={36} />}
      badges={assessment.is_active === false ? [{ label: 'Inactive', variant: 'muted' }] : [{ label: type, variant: 'accent' }]}
      providerLogo={<span className="ux-short-provider-icon"><BookOpen size={14} /></span>}
      providerName={assessment.course_name || assessment.course_title || (assessment.course ? 'Course assessment' : 'Practice assessment')}
      title={assessment.title}
      typeLabel={`${assessment.time_limit_minutes || assessment.duration || 45} min · ${assessment.total_questions || 0} questions`}
    />
  );
}

export function AdminAssessmentShortCard({ assessment, index = 0 }) {
  const type = (assessment.assessment_type || 'quiz').replace(/_/g, ' ');
  const badges = [{ label: type, variant: 'accent' }];
  if (!assessment.is_active) badges.push({ label: 'Inactive', variant: 'muted' });
  if (!assessment.course) badges.push({ label: 'Practice', variant: 'info' });

  return (
    <ShortCard
      to={`/admin/assessments/${assessment.id}`}
      index={index}
      imageFallback={<GraduationCap size={36} />}
      badges={badges}
      providerLogo={<span className="ux-short-provider-icon"><BookOpen size={14} /></span>}
      providerName={assessment.course_name || assessment.course_title || (assessment.course ? 'Course assessment' : 'Practice assessment')}
      title={assessment.title}
      typeLabel={`${assessment.time_limit_minutes || 45} min · ${assessment.total_questions || 0} Qs`}
      footer={(
        <Link
          to={`/admin/assessments/${assessment.id}/questions`}
          className="ux-btn-sm ux-btn-ghost"
          style={{ textDecoration: 'none', marginTop: '0.5rem', width: '100%', justifyContent: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          Manage questions
        </Link>
      )}
    />
  );
}

export function AdminCourseShortCard({ course, index, onManage, onEdit, onDelete }) {
  return (
    <div className="ux-short-card-wrap">
      <CourseShortCard
        course={course}
        index={index}
        onClick={onManage}
        adminOverlay={(
          <div className="ux-short-card-admin-bar">
            <button type="button" className="ux-short-admin-btn" onClick={(e) => { e.stopPropagation(); onEdit?.(); }}>Edit</button>
            <button type="button" className="ux-short-admin-btn danger" onClick={(e) => { e.stopPropagation(); onDelete?.(); }}>Delete</button>
          </div>
        )}
      />
    </div>
  );
}
